import {
  AIDeepInsights,
  AIGoalsReport,
  ConversationGoals,
  UserGoals
} from '@cpd-app/core-module/models';
import { GoalsInput, GoalsReportDetails } from '@cpd-app/generated';
import { createAction, props } from '@ngrx/store';

export const resetGoalState = createAction('[Goal] Reset Goal State');

export const loadGoals = createAction('[Goal] Load Goals');

export const connectGenStatus = createAction('[Goal] Connect Via Insight Gen Status');

export const disConnectGenStatus = createAction('[Goal] Disconnect Via Insight Gen Status');

/**
 * THE REPORT ITSELF FAILED, as reported by the status stream.
 *
 * Distinct from connectGenStatusFailure, which means the STREAM broke. This
 * one carries a fact about the report, and it is a pure state change: no
 * network call, and deliberately NOT genAiGoalsReport(), which is a mutation
 * and would start a fresh generation on every failed frame.
 */
export const genStatusReportFailed = createAction(
  '[Goal] Via Insight Report Failed'
);

export const connectGenStatusFailure = createAction(
  '[Goal] Connect Via Insight Gen Status Failure',
  props<{error: any}>()
);

export const refreshViaInsight = createAction('[Goal] Refresh ViaInsight');

export const goalsLoadedSuccess = createAction(
  '[Goal] Load Goals Success',
  props<{ userGoals: UserGoals }>()
);

export const goalsLoadedFailure = createAction(
  '[Goal] Load Goals Failure',
  props<{ error: any }>()
);

export const loadGoalsReportDetails = createAction(
  '[Goal] Load GoalsReportDetails'
);

export const loadGoalsReportDetailsSuccess = createAction(
  '[Goal] Load GoalsReportDetails Success',
  props<{ data: GoalsReportDetails }>()
);

export const loadGoalsReportDetailsFailure = createAction(
  '[Goal] Load GoalsReportDetails Failure',
  props<{ error: any }>()
);

export const updateGoals = createAction(
  '[Goal] Update Goals',
  props<{ userGoals: Partial<GoalsInput> }>()
);

export const goalsUpdatedSuccess = createAction(
  '[Goal] Update Goals Success',
  props<{ userGoals: UserGoals }>()
);

export const goalsReportLoadedSuccess = createAction(
  '[Goal] Goals Report Success',
  props<{ userGoals: UserGoals }>()
);

export const goalsUpdatedFailure = createAction(
  '[Goal] Update Goals Failure',
  props<{ error: any }>()
);

export const clearAllGoals = createAction('[Goal] Clear All Goals');

export const setCareerGoal = createAction(
  '[Goal] Set Career Goal',
  props<{ onetCode: string; occupationTitle?: string; jobId?: number }>()
);

export const setInstitutionGoal = createAction(
  '[Goal] Set Institution Goal',
  props<{ schoolId: number }>()
);

export const setInstitutionProgramGoal = createAction(
  '[Goal] Set Institution Program Goal',
  props<{
    schoolId: number;
    schoolProgramId: number;
    schoolProgramEdLevelId: number;
  }>()
);

export const setCertificationGoal = createAction(
  '[Goal] Set Certification Goal',
  props<{ certificationId: string }>()
);

/**
 * CRED.1. A Credential Registry credential, from the occupation page carousel.
 *
 * The TITLE travels with the id, unlike every other goal action, and that is
 * not an oversight: nothing in CPD is a catalogue of Registry credentials to
 * resolve a title from later, and the per-occupation cache it came from is
 * rewritten weekly. See the migration.
 */
export const setCredentialGoal = createAction(
  '[Goal] Set Credential Goal',
  // CPD-661 item 5: nullable, because this is also how the goal is REMOVED.
  props<{ credentialCtid: string | null; credentialTitle: string | null }>()
);

export const setLicenseGoal = createAction(
  '[Goal] Set License Goal',
  props<{ licenseId: string }>()
);

export const setReportDownloaded = createAction(
  '[Goal] Set Report Downloaded',
  props<{ reportDownloaded: boolean }>()
);

export const setReportShared = createAction(
  '[Goal] Set Report Shared',
  props<{ reportShared: boolean }>()
);

export const setReportFile = createAction(
  '[Goal] Set Report File',
  props<{ reportFile: string }>()
);

export const genAiGoalsReport = createAction('[Goal] gen Ai Goals Report');

export const genAiGoalsReportSuccess = createAction(
  '[Goal] gen Ai Goals Report Success',
  props<{ aiGoalsReport: AIGoalsReport }>()
);

export const genAiGoalsReportFailure = createAction(
  '[Goal] gen Ai Goals Report Failure',
  props<{ error: any }>()
);

export const setGoalsFromAiChat = createAction(
  '[Goal] Set Goals From Ai Chat',
  props<{ goals: ConversationGoals }>()
);

export const setLearningSuggestionsFromAiChat = createAction(
  '[Goal] Set Learning Suggestions From Ai Chat',
  props<{ conversationId: number, messageId: number }>()
);

export const setLearningSuggestionsFromAiChatSuccess = createAction(
  '[Goal] Set Learning Suggestions From Ai Chat Success',
  props<{ aiDeepInsights: AIDeepInsights }>()
);

export const setLearningSuggestionsFromAiChatFailure = createAction(
  '[Goal] Set Learning Suggestions From Ai Chat Failure',
  props<{ error: any }>()
);

export const loadAiDeepInsight = createAction('[Goal] Load AI Deep Insight');

export const loadAiDeepInsightSuccess = createAction(
  '[Goal] Load AI Deep Insight Success',
  props<{ aiDeepInsights: AIDeepInsights }>()
);

export const loadAiDeepInsightFailure = createAction(
  '[Goal] Load AI Deep Insight Failure',
  props<{ error: any }>()
);
